import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Upload, FileSpreadsheet, Download, AlertCircle, CheckCircle2, AlertTriangle, Copy, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PriceListConverter = ({ onBack }) => {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [error, setError] = useState("");
  const [statusMsg, setStatusMsg] = useState("");

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    setError("");
    setResult(null);
    if (selected && !selected.name.endsWith('.xlsx')) {
      setError("Only .xlsx files are accepted.");
      setFile(null);
      return;
    }
    setFile(selected || null);
  };

  const pollForResult = async (jobId) => {
    const token = localStorage.getItem("token");
    const maxAttempts = 120; // 4 minutes max
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const resp = await fetch(`${API}/admin/convert-pricelist/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) {
          const errData = await resp.json();
          throw new Error(errData.detail || "Conversion failed");
        }
        const data = await resp.json();
        if (data.status === "complete") {
          return data;
        }
        const progress = data.progress || 0;
        const total = data.total || 0;
        if (total > 0) {
          const pct = Math.round((progress / total) * 100);
          setStatusMsg(`AI generating descriptions... ${progress}/${total} products (${pct}%)`);
        } else {
          setStatusMsg(`Reading Excel file...`);
        }
      } catch (err) {
        throw err;
      }
    }
    throw new Error("Conversion timed out. Please try again.");
  };

  const handleConvert = async () => {
    if (!file) return;
    setProcessing(true);
    setError("");
    setResult(null);
    setStatusMsg("Uploading file...");

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/admin/convert-pricelist`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || "Upload failed");
      }

      const { job_id } = await response.json();
      setJobId(job_id);
      setStatusMsg("Processing worksheets & AI generating descriptions...");

      const data = await pollForResult(job_id);
      setResult(data);
      setStatusMsg("");
    } catch (err) {
      setError(err.message || "An error occurred during conversion.");
      setStatusMsg("");
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!jobId) return;
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API}/admin/convert-pricelist/${jobId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const baseName = file?.name?.replace('.xlsx', '') || 'pricelist';
      link.download = `${baseName}_converted.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Download failed. Please convert again.");
    }
  };

  const handleCopyCSV = async () => {
    if (!jobId) return;
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API}/admin/convert-pricelist/${jobId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Copy failed");
      const text = await resp.text();
      navigator.clipboard.writeText(text);
    } catch {
      setError("Copy failed. Please convert again.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6 md:p-10">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-8 text-slate-400 hover:text-white hover:bg-slate-800"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-10">
          <h1 className="text-2xl font-bold text-white tracking-tight">Price List Converter</h1>
          <p className="text-slate-400 mt-1">Upload an Excel price list and convert it to a clean CSV with AI-generated descriptions</p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6 bg-red-950/50 border-red-800" data-testid="error-message">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-red-300">{error}</AlertDescription>
          </Alert>
        )}

        {/* Upload Section */}
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-emerald-400" />
              Upload Excel File
            </CardTitle>
            <CardDescription className="text-slate-400">
              Select an .xlsx file containing your price list
            </CardDescription>
          </CardHeader>
          <CardContent>
            <label
              className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-slate-700 rounded-lg cursor-pointer hover:border-emerald-500/50 hover:bg-slate-800/50 transition-colors"
              data-testid="file-upload-area"
            >
              <Upload className="h-8 w-8 text-slate-500 mb-2" />
              {file ? (
                <p className="text-emerald-400 font-medium text-sm">{file.name}</p>
              ) : (
                <p className="text-slate-500 text-sm">Click to select .xlsx file</p>
              )}
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </label>

            <Button
              onClick={handleConvert}
              disabled={!file || processing}
              className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
              data-testid="convert-button"
            >
              {processing ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {statusMsg || "Converting..."}
                </span>
              ) : (
                "Convert Price List"
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Result Section */}
        {result && (
          <div className="space-y-4" data-testid="conversion-result">
            <Alert className="bg-emerald-950/40 border-emerald-800">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              <AlertDescription className="text-emerald-300">
                Conversion complete — {result.total_rows} product rows extracted
                {result.ai_processed > 0 && ` (${result.ai_processed} descriptions AI-generated)`}
              </AlertDescription>
            </Alert>

            {/* Sheets processed */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-5">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Sheets Processed</p>
                <ul className="space-y-1">
                  {result.sheets_processed.map((s, i) => (
                    <li key={i} className="text-sm text-slate-300">{s}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Warnings */}
            {result.warnings.length > 0 && (
              <Card className="bg-yellow-950/30 border-yellow-800/50">
                <CardContent className="pt-5">
                  <p className="text-xs font-medium text-yellow-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Warnings
                  </p>
                  <ul className="space-y-1">
                    {result.warnings.map((w, i) => (
                      <li key={i} className="text-sm text-yellow-300/80">{w}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Duplicates */}
            {result.duplicates.length > 0 && (
              <Card className="bg-orange-950/30 border-orange-800/50">
                <CardContent className="pt-5">
                  <p className="text-xs font-medium text-orange-500 uppercase tracking-wider mb-2">
                    Duplicates Skipped ({result.duplicates.length})
                  </p>
                  <ul className="space-y-1 max-h-40 overflow-y-auto">
                    {result.duplicates.map((d, i) => (
                      <li key={i} className="text-xs text-orange-300/70 font-mono">{d}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Download */}
            <div className="flex gap-3">
              <Button
                onClick={handleDownload}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="download-csv-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Download CSV
              </Button>
              <Button
                variant="outline"
                onClick={handleCopyCSV}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="copy-csv-button"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceListConverter;
